import {
  DEBUG_OVERRIDE,
  edgeTypeMatrix,
  getEntityProgrammneToEntityEdgeTypeMatrixEntry,
  getEntityRollingProgrammneToEntityEdgeTypeMatrixEntry,
  getEntityToDeviceEdgeTypeMatrixEntry,
  getEntityToPlaceEdgeTypeMatrixEntry,
  getGroupToTypeEdgeTypeMatrixEntry,
  getMinimalVOB,
  Group,
  NOT_LAMBDA,
  System,
  UserAttributeDefs,
} from '@iotv/datamodel-core'
export * from '@iotv/datamodel-core'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

import {
  CoreApplicationObjectType,
  DatastoreObjectType,
  isClass,
  isEdge,
  isValidBusinessObject,
  MapLike,
  TypescriptClass,
  ValidBusinessObject,
} from '@iotv/iotv-v3-types'
import { AggregationGroupFactory, IAggregationGroup } from './AggregationGroupFactory'

import { ModelClasses as iotvModelClasses, ModelClassNames as iotvModelClassNames, BusinessObjectAssemblerFactory } from '@iotv/datamodel-core'

/** Sub Project imports REGEX MARKER GROUP - no empty lines in datamodel blocks */
/** v020 Datamodel */
import { ModelClasses as v020ModelClasses, ModelClassNames as v020ModelClassNames, edgeTypeMatrix as v020EdgeTypeMatrix } from '@v020/datamodel'
/** Sub Project imports REGEX MARKER GROUP - exit */

import { InvalidatedReason, isValidEdgeTypeFnFactory, IsValidEdgeTypeOuput } from './Validation/isValidEdgeType'
import { getDeviceEnabledTypesMap } from './Validation/isDeviceEnabledType'
import { ducktypeStringMap, stringDuckType } from '@iotv/core-services'
import config from '@iotv/config'
import { mutateGetUserAttributes } from './ClassMutation/getUserAttributes'
export { InvalidatedReason } from './Validation/isValidEdgeType'
export type { IsValidEdgeTypeOuput } from './Validation/isValidEdgeType'
export { getDeviceEnabledTypesMap } from './Validation/isDeviceEnabledType'
export { isEUIValid } from './Validation/isEUIValid'

/** ModelClasses REGEX MARKER GROUP - no empty lines */
const ModelClasses = Object.assign({}, iotvModelClasses, v020ModelClasses)

/** ModelClassNames REGEX MARKER GROUP - no empty lines */
export type ModelClassNames = iotvModelClassNames | v020ModelClassNames
/** ModelClassNames REGEX MARKER GROUP - exit */

/** List of names of Model classes that extend DeviceEnabledType --- this does not garauntee that the class has been exposed as deviceEnabledType in \@iotv/payload-definition */
const classDerivedDeviceEnabledTypes = Object.entries(getDeviceEnabledTypesMap(ModelClasses))
  .filter(([k, v]) => v === true)
  .map(([k]) => k)
/** dont allow generic entities where any other datamodel describes a DeviceEnabledType */

if (classDerivedDeviceEnabledTypes.length > 1) {
  const idx = classDerivedDeviceEnabledTypes.findIndex((className) => className === 'Entity')
  classDerivedDeviceEnabledTypes.splice(idx, 1)
}

export { classDerivedDeviceEnabledTypes }
/** matrixes REGEX MARKER GROUP - no empty lines */
const matrixes: MapLike<any>[] = [v020EdgeTypeMatrix]

/** Alternative to isDeviceEnabled type from @iotv/payload-definition, to be used where module dependencies mean no acccess to the original fn */
export const isClassDerivedDeviceEnabledType = <T extends CoreApplicationObjectType>(item: T) => classDerivedDeviceEnabledTypes.includes(item.type)

export type ValidModelType = ModelClassNames | 'AggregationGroup' | 'KMLFileLink'

if ( config.app.webUI.domainMap ) {
    const enumKv = Object.fromEntries( Object.keys( config.app.webUI.domainMap).map( (k) => [ k, k]))
    mutateGetUserAttributes( { clazz: Group, override: {}, extend: {
        getUserAttributes: (): UserAttributeDefs => {
            return {
                domain: { type: 'Enum', editable: true, enumKv, required: true },
            }
        }
    }} )
}


/** 2024 03 Note: EdgeTypeMatrix Mutation
 * this mutates the edgeTypeMatix exported from @iotv/datamodel-core for all consumers
 * file:///./../../datamodel-core/src/classDependentTypes/EdgeTypes/edgeTypeMatrixFns.ts
 * ie non-core, external project edgeTypes are included. As observered in UI Listerlizer where an import from @iotv/datamodel-core of getParentEdgeTypeId correctly
 * resolves external project edges.
 *
 * Look here if unwanted side effects are seen in the future.
 * Resolution is to move edge validation function to this module.
 */
const etm = edgeTypeMatrix as MapLike<any>
try {
  matrixes.forEach((matrix) => {
    Object.entries(matrix).forEach(([sourceType, targetTypeMap]) => {
      if (etm[sourceType]) {
        Object.entries(targetTypeMap).forEach(([targetType, edgeTypeDefMap]) => {
          if (etm[sourceType][targetType]) {
            Object.assign(etm[sourceType][targetType], edgeTypeDefMap)
          } else {
            etm[sourceType][targetType] = edgeTypeDefMap
          }
        })
      } else {
        etm[sourceType] = targetTypeMap
      }
    })
  })
} catch (e: any) {
  console.log('Error merging edgeMatrix', e.message)
}

/** check for deviceEnabledTypes presence in edgeTypeMatrix */
classDerivedDeviceEnabledTypes.forEach((objectTypeId) => {
  // add entry for deviceEnabledType if not present
  if (!etm[objectTypeId]) {
    debug && console.log(`No edgeTypeMatrix entry for deviceEnabledType ${objectTypeId}`)
    const newDeviceEdgeEntry = getEntityToDeviceEdgeTypeMatrixEntry(objectTypeId)
    const newPlaceEdgeEntry = getEntityToPlaceEdgeTypeMatrixEntry(objectTypeId)
    const newEntry = { ...newDeviceEdgeEntry, ...newPlaceEdgeEntry }
    debug && console.log(`New Entry ${objectTypeId} `, { newDeviceEdgeEntry, newPlaceEdgeEntry, newEntry })
    etm[objectTypeId] = newEntry
  } else {
    if (!etm[objectTypeId].Device) {
      etm[objectTypeId].Device = getEntityToDeviceEdgeTypeMatrixEntry(objectTypeId).Place
    }
    if (!etm[objectTypeId].Place) {
      etm[objectTypeId].Place = getEntityToPlaceEdgeTypeMatrixEntry(objectTypeId).Place
    }
  }
  // add Group to deviceEnabledType if not present
  if (!etm.Group[objectTypeId]) {
    const newEntry = getGroupToTypeEdgeTypeMatrixEntry(objectTypeId)
    debug && console.log('New Group entry', newEntry)
    Object.assign(etm.Group, newEntry)
  }

  if (config.apps?.webUI?.showProgrammeSets === true) {
    // // add EntityProgramme to deviceEnabledType if not present
    if (!etm.EntityProgramme[objectTypeId]) {
      const newEntry = getEntityProgrammneToEntityEdgeTypeMatrixEntry(objectTypeId)
      Object.assign(etm.EntityProgramme, newEntry)
    }
    if (!etm.EntityRollingProgramme[objectTypeId]) {
      const newEntry = getEntityRollingProgrammneToEntityEdgeTypeMatrixEntry(objectTypeId)
      // prior to 2025 06 10 this was wrongly assigned to EntityProgramme.
      // the effect would be that all EntityProgramme WaterCannon edges were typed as 'rolling_programme_schedules_WaterCannon' amd all rolling programme edge had undefined edgeTypeId
      //  Data checked OK in  v021 TEST, PROD with all instances showing correct value of 'programme_schedules_WaterCannon'
      // issue found when running file:///./../../../Utilities/src/Edges/correctEdgeTypes.ts
      Object.assign(etm.EntityRollingProgramme, newEntry)
    }
  }
})

Object.entries(etm).forEach(([sourceType, targetTypeMap]) => {
  if (classDerivedDeviceEnabledTypes.includes(sourceType)) {
    /** check for device relationship */
    if (!targetTypeMap.Device) {
      debug && console.log(`${sourceType} has no edgeType to device`)
      const newEntry = getEntityToDeviceEdgeTypeMatrixEntry(sourceType)
      debug && console.log('New Entry', newEntry)
      Object.assign(etm[sourceType], newEntry)
      //
    }
  }
})

export { edgeTypeMatrix }

export type ValidModelObject<T extends ValidModelType> = ValidBusinessObject & {
  type: T
}

export type ValidModelObjects<T extends ValidModelType> = ValidModelObject<T>[]

const BusinessObjectAssembler = BusinessObjectAssemblerFactory.createBusinessObjectAssembler(ModelClasses)
export { BusinessObjectAssembler }

const Assembler = new BusinessObjectAssembler()
export { Assembler }

const AggregationGroup = AggregationGroupFactory(ModelClasses)
export type { IAggregationGroup }
export { AggregationGroup }

const ModelClassList = Object.keys({ ...ModelClasses, AggregationGroup, System })

function isClassLocal<T>(value: unknown): value is TypescriptClass<T> {
  const isClass = typeof value === 'function' && !!value.prototype && !!value.prototype.constructor
  if (!isClass) {
    console.log(`is not class`, value)
  } else {
    debug && console.log(`is class`, value)
  }
  return isClass
}

/** WIP these will move to full datamodel definition in \@iotv/typed */
const isValidImportedModelClassNames = (ModelClassNames: any) => ModelClassNames instanceof Array && ModelClassNames.every((item) => typeof item === 'string')
const isValidModelClasses = (ModelClasses: unknown): ModelClasses is TypescriptClass<any> =>
  !!ModelClasses && typeof ModelClasses === 'object' && Object.entries(ModelClasses).every(([k, v]) => typeof k === 'string' && isClassLocal(v))

/* END OF VERY TEMP TYPE CHECK */
if (!isValidImportedModelClassNames(ModelClassList)) {
  throw new Error('INVALID ModelClassList')
}
if (!isValidModelClasses(ModelClasses)) {
  const invalidModelClassExports: { [k: string]: string } = {}
  Object.entries(ModelClasses).forEach(([k, v]) => {
    if (!isClass(v)) {
      const hasProtoype = !!(v as any).prototype
      const hasConstructor = !!(v as any).prototype?.constructor
      const reason = `${k} is not a class. It is type ${typeof v}. ${hasProtoype ? '' : 'Has no prototype'}. ${hasConstructor ? '' : 'Has no prootype.constructor'}`
      invalidModelClassExports[k] = reason
    }
  })
  console.log(`Invalid exports found in @iotv/datamodel ModelClasses. The check that all exports are classes failed with:`, invalidModelClassExports)
  console.log(
    `If these are  enumerations or other constants, they should be moved external from /src/model, added to the src/index as exports and imported to the original location`,
  )
  // throw new Error( 'INVALID ModelClasses')
}

export { ModelClassList, ModelClasses }

export const isValidModelTypeId = (objectTypeId: string): objectTypeId is ValidModelType => ModelClassList.includes(objectTypeId)

export const isValidEdgeType = isValidEdgeTypeFnFactory({ businessObjectAssembler: new BusinessObjectAssembler(), edgeTypeMatrix })

type IsValidEdgeInstanceInput = { item: Record<string, any> }
export const isValidEdgeInstance = ({ item }: IsValidEdgeInstanceInput): IsValidEdgeTypeOuput => {
  let res: IsValidEdgeTypeOuput = { isValid: false }
  if (isValidBusinessObject(item) && isEdge(item)) {
    const sourceObjectTypeId = item.pk.split(':')[0]
    const targetObjectTypeId = item.sk.split(':')[0]
    res = isValidEdgeType({ sourceObjectTypeId, targetObjectTypeId })
  } else {
    res.reason = InvalidatedReason.INVALID_TYPES
    res.violation = `Item either not edge or not valid business object, ${JSON.stringify(item)}`
  }

  return res
}
